import publicRequest from "../configs/axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

let firebaseContextPromise = null;

const getFirebaseContext = async () => {
  if (!firebaseContextPromise) {
    firebaseContextPromise = Promise.all([
      import("firebase/auth"),
      import("../Pages/AuthPage/Firebase/firebase.init"),
    ]).then(([authModule, initModule]) => {
      initModule.default();

      return {
        auth: authModule.getAuth(),
        googleProvider: new authModule.GoogleAuthProvider(),
        gitHubProvider: new authModule.GithubAuthProvider(),
        authModule,
      };
    });
  }

  return firebaseContextPromise;
};

// Human-friendly messages for the most common Firebase auth errors so the
// change-password form can tell the user exactly what went wrong.

const friendlyAuthError = (error) => {
  const code = error?.code || "";
  switch (code) {
    case "auth/wrong-password":
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
      return "Your current password is incorrect.";
    case "auth/weak-password":
      return "New password must be at least 6 characters.";
    case "auth/user-mismatch":
    case "auth/user-not-found":
    case "auth/user-token-expired":
    case "auth/requires-recent-login":
      return "Your session expired. Please log in again and retry.";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later.";
    default:
      return error?.message || "Failed to change password.";
  }
};

const useFirebase = () => {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisable, setIsDisable] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [token, setToken] = useState(
    () => localStorage.getItem("kacha_bazar_token") || "",
  );

  // google signIn/signUp function

  const googleSignIn = async (navigate, location) => {
    setIsDisable(true);
    try {
      const { auth, googleProvider, authModule } = await getFirebaseContext();
      const userCredential = await authModule.signInWithPopup(
        auth,
        googleProvider,
      );
      toast.success("Logged in successfully...");
      setLoggedInUser(userCredential.user);
      saveUserForOthers(
        userCredential.user.email,
        userCredential.user.displayName,
      );
      const redirect_URI = location.state?.from || "/";
      navigate(redirect_URI);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsDisable(false);
      setIsLoading(false);
    }
  };

  // gitHub signIn/signUp function

  const gitHubSignIn = async (navigate, location) => {
    setIsDisable(true);
    try {
      const { auth, gitHubProvider, authModule } = await getFirebaseContext();
      const userCredential = await authModule.signInWithPopup(
        auth,
        gitHubProvider,
      );
      toast.success("Logged in successfully...");
      setLoggedInUser(userCredential.user);
      saveUserForOthers(
        userCredential.user.email,
        userCredential.user.displayName,
      );
      const redirect_URI = location.state?.from || "/";
      navigate(redirect_URI);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsDisable(false);
      setIsLoading(false);
    }
  };

  // email password signUp function

  const emailSignup = async (name, email, password, navigate) => {
    setIsDisable(true);
    const loading = toast.loading("Creating User... Please wait!!!");
    try {
      const { auth, authModule } = await getFirebaseContext();
      const userCredential = await authModule.createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      await authModule.updateProfile(auth.currentUser, {
        photoURL: "https://i.ibb.co/7CzR0Dg/users.jpg",
        displayName: name,
      });
      saveUserForEmail(email, name);
      toast.dismiss(loading);
      toast.success("Creating a new user successfully...");
      setLoggedInUser(userCredential.user);
      navigate("/");
    } catch (error) {
      toast.dismiss(loading);
      toast.error(error.message);
    } finally {
      setIsDisable(false);
      setIsLoading(false);
    }
  };

  // email password signIn function

  const emailSignIn = async (email, password, navigate, location, e) => {
    setIsDisable(true);
    const loading = toast.loading("Finding Account... Please wait!!!");
    try {
      const { auth, authModule } = await getFirebaseContext();
      const userCredential = await authModule.signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      toast.dismiss(loading);
      toast.success("logged in successfully...");
      setLoggedInUser(userCredential.user);
      e.target.reset();
      const redirect_URI = location.state?.from || "/";
      navigate(redirect_URI);
    } catch (error) {
      toast.dismiss(loading);
      toast.error(error.message);
    } finally {
      setIsDisable(false);
      setIsLoading(false);
    }
  };

  // save user to mongoDB (Email)

  const saveUserForEmail = (email, displayName) => {
    setIsDisable(true);
    const user = { email, displayName };
    publicRequest
      .post("/user", user)
      .then((res) => {
        if (res.data.upsertedId) {
          toast.success("User Added in our Database Successfully!");
        }
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setIsDisable(false));
  };

  // save user to mongoDB (Gmail, GitHub)

  const saveUserForOthers = (email, displayName) => {
    setIsDisable(true);
    const user = { email, displayName };
    publicRequest
      .put("/user", user)
      .then((res) => {
        if (res.data.upsertedId) {
          toast.success("User Added in our Database Successfully!");
        }
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setIsDisable(false));
  };

  // Reset Password Function

  const resetPassword = async (email, e) => {
    setIsDisable(true);
    try {
      const { auth, authModule } = await getFirebaseContext();
      await authModule.sendPasswordResetEmail(auth, email);
      toast.success("Check your gmail inbox. We send an verification email");
      e.target.reset();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsDisable(false);
      setIsLoading(false);
    }
  };

  // Change Password Function (re-authenticate with the current password
  // first, then update it — Firebase requires a recent login for this)

  const changePassword = async (currentPassword, newPassword, form) => {
    setIsDisable(true);
    const loading = toast.loading("Updating Password... Please wait!!!");
    try {
      const { auth, authModule } = await getFirebaseContext();
      const user = auth.currentUser;
      if (!user) {
        throw new Error("You must be logged in to change your password.");
      }
      const credential = authModule.EmailAuthProvider.credential(
        user.email,
        currentPassword,
      );
      await authModule.reauthenticateWithCredential(user, credential);
      await authModule.updatePassword(user, newPassword);
      toast.dismiss(loading);
      toast.success("Password changed successfully...");
      if (form?.reset) form.reset();
    } catch (error) {
      toast.dismiss(loading);
      toast.error(friendlyAuthError(error));
    } finally {
      setIsDisable(false);
      setIsLoading(false);
    }
  };

  // signOut function

  const logOut = async () => {
    try {
      const { auth, authModule } = await getFirebaseContext();
      await authModule.signOut(auth);
      setLoggedInUser(null);
      localStorage.removeItem("kacha_bazar_token");
      setToken("");
      toast.error("Logged Out!!!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // observe user state change

  useEffect(() => {
    let unSubscribed = () => {};
    let mounted = true;

    void getFirebaseContext()
      .then(({ auth, authModule }) => {
        if (!mounted) {
          return;
        }

        unSubscribed = authModule.onAuthStateChanged(auth, (user) => {
          if (user) {
            setLoggedInUser(user);
            authModule.getIdToken(user).then((idToken) => {
              localStorage.setItem("kacha_bazar_token", idToken);
              setToken(idToken);
            });
            // Persist identity for Facebook Conversions API advanced
            // matching (read by useFacebookPixel.getUserData)
            if (user.email) {
              localStorage.setItem("userEmail", user.email);
            }
            if (user.phoneNumber) {
              localStorage.setItem("userPhone", user.phoneNumber);
            }
            if (user.uid) {
              localStorage.setItem("fb_external_id", user.uid);
            }
          } else {
            localStorage.removeItem("kacha_bazar_token");
            localStorage.removeItem("userEmail");
            localStorage.removeItem("userPhone");
            localStorage.removeItem("fb_external_id");
            setToken("");
          }
          setIsLoading(false);
        });
      })
      .catch(() => {
        if (mounted) {
          setIsLoading(false);
        }
      });

    return () => {
      mounted = false;
      unSubscribed();
    };
  }, []);

  // Check Admin or not

  useEffect(() => {
    if (loggedInUser?.email) {
      publicRequest
        .get(`/user/${loggedInUser.email}`)
        .then((res) => setIsAdmin(res.data.admin))
        .catch((err) => toast.error(err.message))
        .finally(() => setIsLoading(false));
    }
  }, [loggedInUser?.email]);

  return {
    isLoading,
    loggedInUser,
    isAdmin,
    isDisable,
    token,
    googleSignIn,
    gitHubSignIn,
    emailSignup,
    emailSignIn,
    resetPassword,
    changePassword,
    logOut,
  };
};

export default useFirebase;
